/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

/**
 * The standard section-header action - a word plus an icon, in the primary colour, at the right of
 * a `.sec-head`. Plain text (`font: inherit`, no border or background) so it keeps the header's
 * baseline and height; it goes bold on hover to read as clickable. `label` / `icon` set what it
 * says and shows (e.g. "Edit" + edit, "Expand" + zoom_out_map). Emits `act` on click.
 */
@Component({
    selector: 'app-section-action-button',
    imports: [MatIconModule, MatTooltipModule],
    template: `
        <button type="button" class="section-action primary-font-color"
                [attr.data-section-action]="testId || null"
                [matTooltip]="tip" [disabled]="disabled" (click)="act.emit()"
        >{{ label }}<mat-icon>{{ icon }}</mat-icon></button>
    `,
    styles: [`
        :host {
            margin-left: auto;
        }
        button.section-action {
            padding: 0;
            border: 0;
            background: none;
            font: inherit;
            line-height: inherit;
            letter-spacing: inherit;
            text-transform: inherit;
            cursor: pointer;
        }
        button.section-action:hover {
            font-weight: bold;
        }
        button.section-action mat-icon {
            font-size: 14px;
            height: 14px;
            width: 14px;
            margin-left: 3px;
            vertical-align: text-bottom;
        }
        button.section-action:disabled {
            opacity: 0.5;
            cursor: default;
            font-weight: inherit;
        }
    `]
})
export class SectionActionButtonComponent {
    @Input() label = $localize`Edit`;
    @Input() icon = 'edit';
    @Input() tip = '';
    @Input() disabled = false;
    /** Stable test hook — rendered as data-section-action so Playwright can target the button. */
    @Input() testId = '';
    @Output() act = new EventEmitter<void>();
}
